import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LayoutGrid } from 'lucide-react';

function weakCountForSubject(insights, subjectName) {
  if (!subjectName || !insights?.weak_topics?.length) return 0;
  const n = subjectName.toLowerCase();
  return insights.weak_topics.filter((w) => w.topic && w.topic.toLowerCase().includes(n.slice(0, 4))).length;
}

/**
 * Merge study priorities (when API provides them) with lecture counts.
 * Falls back to flat subject list for a calm “term snapshot”.
 */
export default function SubjectSnapshotGrid({ subjects, lectures, studyDashboard, insights }) {
  const priorities = studyDashboard?.priorities || [];
  const lectBySubject = (sid) => (lectures || []).filter((l) => l.subject_id === sid).length;

  let rows = [];
  if (priorities.length > 0) {
    rows = priorities.map((p) => ({
      id: p.subject_id,
      name: p.subject_name,
      code: p.course_code,
      color: p.color,
      priorityScore: p.priority_score,
      lectures: lectBySubject(p.subject_id),
      weakHints: weakCountForSubject(insights, p.subject_name),
    }));
  } else if ((subjects || []).length > 0) {
    rows = subjects.map((s) => ({
      id: s.id,
      name: s.name,
      code: s.course_code,
      color: s.color,
      priorityScore: null,
      lectures: lectBySubject(s.id),
      weakHints: weakCountForSubject(insights, s.name),
    }));
  }

  return (
    <Card className="border-border/60 shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-4 h-4 text-muted-foreground" aria-hidden />
          <CardTitle className="font-serif text-lg font-semibold">Subject snapshots</CardTitle>
        </div>
        <CardDescription>
          Per-course pulse for this term — lectures captured and study urgency when available.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10 rounded-lg border border-dashed border-border/70 bg-muted/20">
            Add subjects under <span className="text-foreground">Subjects</span> to see snapshots here.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {rows.map((row) => (
              <div
                key={row.id}
                className="rounded-xl border border-border/50 bg-muted/10 px-4 py-3 flex flex-col gap-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                        style={{ backgroundColor: row.color || 'hsl(var(--muted-foreground))' }}
                        aria-hidden
                      />
                      <div>
                        <p className="font-medium text-sm text-foreground truncate">{row.name}</p>
                        {row.code && (
                          <p className="text-[11px] text-muted-foreground font-mono">{row.code}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  {row.priorityScore != null && (
                    <span className="text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                      Priority{' '}
                      <span className="font-medium text-foreground">{row.priorityScore}</span>
                    </span>
                  )}
                </div>
                <dl className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <dt className="text-muted-foreground">Lectures</dt>
                    <dd className="font-medium tabular-nums text-foreground">{row.lectures}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Weak signals</dt>
                    <dd className="font-medium tabular-nums text-foreground">{row.weakHints}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
